import React from 'react';
import { deleteStatsWidget, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, IUserStore, } from '@Store';
import { CrudEnum, IPreviewStatsWidget, PermissionEnum, EnumTranslateTitle, EnumListSearchProps, TStatsWidgetSortableListCol, StatsWidgetTypeEnum, } from 'atlas-shared';
import { getFullName, tableColumnDatetimeSorter, tableColumnStringSorter, timeSince, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchLikeProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const StatsWidgetListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, users: IUserStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewStatsWidget) => React.ReactElement> = [], filters: IWithListProps<IPreviewStatsWidget, TStatsWidgetSortableListCol>['filters'], sorter: IWithListProps<IPreviewStatsWidget, TStatsWidgetSortableListCol>['sorter']): ColumnsType<IPreviewStatsWidget> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewStatsWidget, b: IPreviewStatsWidget) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewStatsWidget['organization_id'], stats_widget: IPreviewStatsWidget) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewStatsWidget, b: IPreviewStatsWidget) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('USER'),
      dataIndex: 'user_id',
      key: 'user_id',
      render: (user_id: IPreviewStatsWidget['user_id'], stats_widget: IPreviewStatsWidget) => user_id === -1 ? t('SYS_USER') : users.dict?.[user_id] ? getFullName(users.dict[user_id]) : t('USER_NOT_FOUND', { replace: { user_id, }, }),
      sorter: (a: IPreviewStatsWidget, b: IPreviewStatsWidget) => (users.dict?.[a.user_id] ? a.user_id === -1 ? t('SYS_USER') : getFullName(users.dict?.[a.user_id]) : '').localeCompare(users.dict?.[b.user_id] ? getFullName(users.dict?.[b.user_id]) : ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'user_id')?.order || null,
      filteredValue: filters.user_id || null,
      ...GetColumnSearchProps(t, 'user_id', t('USER'), users.users),
      className: 'table-cell-user_id',
    },
    {
      title: t('TITLE'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IPreviewStatsWidget, b: IPreviewStatsWidget) => tableColumnStringSorter(a.title, b.title),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'title')?.order || null,
      filteredValue: filters.title || null,
      ...GetColumnSearchLikeProps(t, 'title', t('TITLE')),
      className: 'table-cell-title',
    },
    {
      title: t('TYPE'),
      dataIndex: 'type',
      key: 'type',
      render: (type: IPreviewStatsWidget['type'], stats_widget: IPreviewStatsWidget) => EnumTranslateTitle('STATS_WIDGET_TYPE', type, t),
      sorter: (a: IPreviewStatsWidget, b: IPreviewStatsWidget) => EnumTranslateTitle('STATS_WIDGET_TYPE', a.type, t).localeCompare(EnumTranslateTitle('STATS_WIDGET_TYPE', b.type, t)),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'type')?.order || null,
      filteredValue: filters.type || null,
      ...GetColumnSearchProps(t, 'type', t('TYPE'), EnumListSearchProps('STATS_WIDGET_TYPE', StatsWidgetTypeEnum, t)),
      className: 'table-cell-type',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewStatsWidget['created_at'], stats_widget: IPreviewStatsWidget) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewStatsWidget, b: IPreviewStatsWidget) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewStatsWidget) => (
        <div className={'table-operation-wrp'}>
          {buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'stats_widget', row)}
          {abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.StatsWidget, row) && listEditButton(t, navigate, 'stats_widget', row)}
          {abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.StatsWidget, row) && listDeleteButton(t, row, deleteStatsWidget)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewStatsWidget>;

};
